import { defaultQueryParamName } from '../sharedConstants';
import type { SearchPageFilterDefinition } from '../sharedTypes';
import { getParsedQueryFromAsPath, getQueryValues, hasQueryParam } from '../urlState';
import { createDefaultFilterSelections, sanitizeFilterValues } from './filterState';
import type { SearchCriteriaUrlState } from './types';

type ParseSearchCriteriaUrlStateArgs<FilterName extends string> = {
  query: Record<string, string | string[] | undefined>;
  filterDefinitions: Record<FilterName, SearchPageFilterDefinition>;
  filterNames: FilterName[];
  queryParamName?: string;
};

export const parseSearchCriteriaUrlState = <FilterName extends string>(
  args: ParseSearchCriteriaUrlStateArgs<FilterName>
): SearchCriteriaUrlState<FilterName> => {
  const { query, filterDefinitions, filterNames, queryParamName = defaultQueryParamName } = args;
  const defaultFilters = createDefaultFilterSelections(filterDefinitions, filterNames);

  const parsedFilters = filterNames.reduce<Record<FilterName, string[]>>(
    (selectedFilters, filterName) => {
      const definition = filterDefinitions[filterName];
      const paramName = definition.param ?? filterName;

      selectedFilters[filterName] = hasQueryParam(query, paramName)
        ? sanitizeFilterValues(definition, getQueryValues(query[paramName]))
        : defaultFilters[filterName];

      return selectedFilters;
    },
    {} as Record<FilterName, string[]>
  );

  return {
    query: getQueryValues(query[queryParamName])[0]?.trim() ?? '',
    filters: parsedFilters,
  };
};

type ReadSearchCriteriaUrlStateArgs<FilterName extends string> = Omit<
  ParseSearchCriteriaUrlStateArgs<FilterName>,
  'query'
> & {
  asPath: string;
};

export const readSearchCriteriaUrlStateFromAsPath = <FilterName extends string>(
  args: ReadSearchCriteriaUrlStateArgs<FilterName>
) => {
  const { asPath, ...parseArgs } = args;

  return parseSearchCriteriaUrlState({
    ...parseArgs,
    query: getParsedQueryFromAsPath(asPath),
  });
};

type AppendSearchCriteriaParamsArgs<FilterName extends string> = {
  params: URLSearchParams;
  urlState: SearchCriteriaUrlState<FilterName>;
  filterDefinitions: Record<FilterName, SearchPageFilterDefinition>;
  filterNames: FilterName[];
  queryParamName?: string;
};

export const appendSearchCriteriaParams = <FilterName extends string>(
  args: AppendSearchCriteriaParamsArgs<FilterName>
) => {
  const {
    params,
    urlState,
    filterDefinitions,
    filterNames,
    queryParamName = defaultQueryParamName,
  } = args;

  if (urlState.query) {
    params.set(queryParamName, urlState.query);
  }

  filterNames.forEach((filterName) => {
    const paramName = filterDefinitions[filterName].param ?? filterName;

    urlState.filters[filterName].forEach((value) => {
      params.append(paramName, value);
    });
  });
};
